import { HistorialImportaciones } from '../../componentes/HistorialImportaciones.tsx';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { Cargando, EncabezadoPagina, Tarjeta as TarjetaUi, Avatar } from '../../componentes/ui.tsx';

interface Resumen {
  totalPosts: number;
  porDia: { fecha: string; posts: number }[];
  porMarca: { marca: string; posts: number }[];
  porFormato: { formato: string; posts: number }[];
  porAccion: { accion: string; posts: number }[];
  topReferencias: { referencia: string; veces: number }[];
  topPlantillas: { plantilla: string; posts: number }[];
  clientesActivos: number;
  clientesDormidos: number;
  clientesSinPosts: { id: string; razonSocial: string; ruc: string }[];
}

function Barras({ datos }: { datos: { etiqueta: string; valor: number }[] }) {
  const maximo = Math.max(1, ...datos.map((d) => d.valor));
  return (
    <ul className="space-y-1">
      {datos.map((d) => (
        <li key={d.etiqueta} className="flex items-center gap-2 text-xs">
          <span className="w-28 shrink-0 truncate">{d.etiqueta}</span>
          <span
            className="h-3 rounded-full"
            style={{ width: `${(d.valor / maximo) * 100}%`, minWidth: 6, backgroundColor: 'var(--color-acento-var)' }}
          />
          <span className="text-suave">{d.valor}</span>
        </li>
      ))}
      {datos.length === 0 ? <li className="text-xs text-suave">Sin datos todavía</li> : null}
    </ul>
  );
}

function Tarjeta({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return <TarjetaUi titulo={titulo}>{children}</TarjetaUi>;
}

/** Analítica (D19, §12): alimentada por PostGenerado, sin archivos (D30). */
export function PaginaAdminAnalitica() {
  const resumen = useQuery({
    queryKey: ['admin-analitica'],
    queryFn: () => api<Resumen>('/admin/analitica/resumen?dias=30'),
  });

  if (resumen.isPending) return <Cargando texto="Cargando…" />;
  const d = resumen.data;
  if (!d) return null;

  return (
    <main className="mx-auto w-full max-w-[1600px] space-y-4">
      <EncabezadoPagina titulo="Analítica" subtitulo="Últimos 30 días · sin archivos, solo metadatos (D30)" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {(
          [
            ['Posts generados', d.totalPosts, 'negra'],
            ['Clientes activos', d.clientesActivos, 'acento'],
            ['Clientes dormidos', d.clientesDormidos, 'blanca'],
          ] as const
        ).map(([etiqueta, valor, tono]) => (
          <div key={etiqueta} className={`${tono === 'negra' ? 'tarjeta-negra' : tono === 'acento' ? 'tarjeta-acento' : 'tarjeta'} p-5`}>
            <p className={`text-sm ${tono === 'blanca' ? 'text-suave' : 'opacity-60'}`}>{etiqueta}</p>
            <p className="mt-1 text-[40px] font-light leading-none">{valor}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Tarjeta titulo="Posts por día">
          <Barras datos={d.porDia.map((x) => ({ etiqueta: x.fecha.slice(5), valor: x.posts }))} />
        </Tarjeta>
        <Tarjeta titulo="Por marca">
          <Barras datos={d.porMarca.map((x) => ({ etiqueta: x.marca, valor: x.posts }))} />
        </Tarjeta>
        <Tarjeta titulo="Top referencias promocionadas">
          <Barras datos={d.topReferencias.map((x) => ({ etiqueta: x.referencia, valor: x.veces }))} />
        </Tarjeta>
        <Tarjeta titulo="Plantillas más usadas">
          <Barras datos={d.topPlantillas.map((x) => ({ etiqueta: x.plantilla, valor: x.posts }))} />
        </Tarjeta>
        <Tarjeta titulo="Formato">
          <Barras datos={d.porFormato.map((x) => ({ etiqueta: x.formato, valor: x.posts }))} />
        </Tarjeta>
        <Tarjeta titulo="Canal de salida">
          <Barras datos={d.porAccion.map((x) => ({ etiqueta: x.accion, valor: x.posts }))} />
        </Tarjeta>
      </div>

      <Tarjeta titulo={`Clientes que nunca generaron un post (${d.clientesSinPosts.length})`}>
        <ul className="divide-y divide-superficie2 text-sm">
          {d.clientesSinPosts.map((c) => (
            <li key={c.id} className="flex items-center gap-3 py-2">
              <Avatar nombre={c.razonSocial} className="h-9 w-9 text-xs" />
              <span className="flex-1">{c.razonSocial}</span>
              <span className="tag font-mono">{c.ruc}</span>
            </li>
          ))}
        </ul>
      </Tarjeta>
      <HistorialImportaciones />

    </main>
  );
}
